import { defaults, find } from 'lodash';
import videojs from 'video.js';
import type { Player, PlayerOptions, videojsCourseraOptions } from 'video.js';

import supportsNativeTextTracks from 'pages/open-course/video/util/supportsNativeTextTracks';

import 'bundles/videojs/plugins/circlePlayButton';
import 'bundles/videojs/plugins/fullScreenButton';
import 'bundles/videojs/plugins/muteToggle';
import 'bundles/videojs/plugins/playToggle';
import 'bundles/videojs/plugins/settingsMenuButton';
import 'bundles/videojs/plugins/subtitlesMenuButton';
import 'bundles/videojs/plugins/volumeMenuButton';

export const getFlexDefaults = function (
  videojsCourseraOptions: Pick<videojsCourseraOptions, 'videoSubtitlesDisabled' | 'defaultSubtitleLanguage'> = {}
): PlayerOptions {
  // We're going to go ahead and replace the elements of any buttons with
  // icons so that we can use font-awesome class-based icons instead.
  // These elements are too small to put in their own files, so we use one
  // file to house them all.

  return {
    fluid: true,
    controls: true,
    html5: {
      nativeTextTracks: supportsNativeTextTracks(),
    },
    children: {
      mediaLoader: {},
      posterImage: {},
      textTrackDisplay: {},
      loadingSpinner: {},
      circlePlayButton: {},
      controlBar: {
        children: [
          'progressControl',
          'cPlayToggle',
          'currentTimeDisplay',
          'timeDivider',
          'durationDisplay',
          ...(!videojsCourseraOptions.videoSubtitlesDisabled
            ? [
                {
                  name: 'subtitlesMenuButton',
                  ...(videojsCourseraOptions.defaultSubtitleLanguage
                    ? { defaultSubtitleLanguage: videojsCourseraOptions.defaultSubtitleLanguage }
                    : {}),
                },
              ]
            : []),
          {
            name: 'cVolumeMenuButton',
            inline: false,
            vertical: true,
          },
          'settingsMenuButton',
          'cFullscreenToggle',
        ],
      },
      errorDisplay: {},
      textTrackSettings: {},
    },
  };
};

const attachListeners = function (player: Player) {
  player.one('play', function () {
    if (player.autoplay()) {
      (player.el_ as HTMLElement)?.focus();
    }
  });
};

const VideoPlayer = function (
  id: any,
  videojsPlayerOptions: PlayerOptions & { defaultSubtitleLanguage?: string } = {}
): Player {
  const options: PlayerOptions = defaults(
    videojsPlayerOptions,
    getFlexDefaults({ defaultSubtitleLanguage: videojsPlayerOptions.defaultSubtitleLanguage })
  );

  const player = videojs(id, options);
  attachListeners(player);

  // @ts-expect-error TSMIGRATION
  player.currentTrack = function () {
    return find(
      player
        // @ts-expect-error TSMIGRATION
        .textTracks(),
      (track) => track.mode === 'showing'
    );
  };

  return player;
};

export default VideoPlayer;
